import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '../notifications/toast';
import { useVersionCheck } from './useVersionCheck';

/**
 * Watches for a newer deployed version while the tab stays open and, on the
 * first detection, shows a persistent toast inviting the user to reload. The
 * reload is never forced so in-progress, unsaved work is preserved.
 */
export default function VersionWatcher() {
  const { t } = useTranslation();
  const toast = useToast();
  const { isOutdated, deployedVersion } = useVersionCheck();
  // Remember the deployed version we last prompted for so dismissing the toast
  // stops the nag for that version, while a later (newer) deploy prompts again.
  const lastPromptedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isOutdated || !deployedVersion) {
      return;
    }
    if (lastPromptedRef.current === deployedVersion) {
      return;
    }
    lastPromptedRef.current = deployedVersion;
    toast.warning(t('version.update_available.title', 'A new version is available'), {
      description: t(
        'version.update_available.description',
        'Reload the page to get the latest updates.',
      ),
      duration: 0,
      action: {
        label: t('version.update_available.action', 'Reload'),
        onClick: () => window.location.reload(),
      },
    });
  }, [isOutdated, deployedVersion, toast, t]);

  return null;
}
